/**
 * What the theatre's hero can show: the demo, and every material that is a YouTube film.
 *
 * Read from the links ON SCREEN — the materials block's own list, the one a save just replaced — so
 * a film the trainer adds or removes appears or leaves as a tab without the card reopening. A tab
 * the trainer was watching that stops existing falls back to the demo (`sourceIn`), never to
 * another film they did not pick.
 *
 * A key is built from the film's id and start rather than the address or the position: it ends up
 * in an element id, so it may only carry YouTube's own id alphabet, and a position would hand a
 * removed tab's selection to the film that slid into its place.
 */

import { youtubeVideoOf, type ExerciseLink, type YouTubeVideo } from '~/entities/exercise-library';

export const DEMO_SOURCE = 'source-demo';

export interface FilmSource {
  readonly key: string;
  readonly url: string;
  /** The trainer's words for it, or its host where they gave none — the materials row's own rule. */
  readonly label: string;
  readonly video: YouTubeVideo;
}

export function filmSourcesOf(links: readonly ExerciseLink[]): FilmSource[] {
  const sources: FilmSource[] = [];
  const taken = new Set<string>();
  for (const link of links) {
    const video = youtubeVideoOf(link.url);
    if (video === null) continue;
    let key = `source-yt-${video.id}-${String(video.startSeconds)}`;
    // Two materials naming the same moment of the same film are still two tabs.
    while (taken.has(key)) key = `${key}-b`;
    taken.add(key);
    sources.push({
      key,
      url: link.url,
      label: link.label === '' ? link.displayHost : link.label,
      video,
    });
  }
  return sources;
}

/** The film a key names among today's sources, or `null` for the demo — including a key gone stale. */
export function sourceIn(sources: readonly FilmSource[], key: string): FilmSource | null {
  return sources.find((source) => source.key === key) ?? null;
}
